import { Checkout, ReporterUser, SummaryItem } from '@wix/checkout-components';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { Text } from '@wix/design-system';
import { CheckoutSummaryFooter } from 'components/CheckoutSummaryFooter/CheckoutSummaryFooter';
import { withGtmLogger } from 'components/HOCs/withGtmLogger';
import { PageFooter } from 'components/PageFooter/PageFooter';
import { AppFlow, Experiments, PaymentCycle } from 'enums';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { CheckoutPageController } from './CheckoutPage.controller';
import { CheckoutPageProps } from './CheckoutPageProps';
import { TermOfUse } from './CheckoutDisclaimer/TermOfUse';
import s from './CheckoutPage.scss';
import { DoublePurchaseModal } from './DoublePurchaseModal/DoublePurchaseModal';
import { CreditExplanation } from './CreditComponent/CreditExplanation';
import { creditExplanationTooltipData } from 'utils';
import { CheckoutRowLabel, CheckoutRowLabelProps } from './CheckoutRowLabel';
import { HttpClient } from '@wix/http-client';

const CheckoutPageComp: React.FC<CheckoutPageProps> = (props) => {
  const controller = React.useMemo(() => new CheckoutPageController(props), []);
  const { t, experiments } = props;
  const { theme: themeName } = usePageModeContext();
  const {
    beforeSubmit,
    handlePurchaseError,
    handleInitError,
    checkoutInitCompleted,
    paymentSuccessful,
    onSubmitPending,
    initOrderPage,
    beforeInit,
    isDoublePurchaseModalOpened,
    additionalDoublePurchaseInfo,
    closeDoublePurchaseModal,
    getCouponLabel,
    setShouldShowCustomDisclaimer,
    shouldShowCustomDisclaimer,
  } = controller!;
  const {
    appContext,
    isEditorXSpecialDiscountEnabled,
    isVoucherDisclaimerHidden,
    isSecondUpgrade,
    currentProduct
  } = props.rootStore.appStore;
  const { biLogger, packagePickerType } = appContext;

  const [currencyToString] = usePremiumCurrencyFormatter();

  const userDetails: ReporterUser | undefined = appContext.isAnonymous
    ? undefined
    : { id: appContext.userGuid, username: appContext.username };

  const getSummaryItems = (formatCurrency, items: SummaryItem[]) => {
    const { currentProduct, currentCycle } = props.rootStore.appStore;
    controller.changeCycleIfRequired(items);
    const {
      currentCycleMultiplier,
      showStrikedPrice,
      showDiscountSavings,
      currentProductCycle,
      monthlyFullPrice,
    } = controller;
    const cycleString =
      currentCycle === PaymentCycle.MONTHLY
        ? `/ ${t('checkout.summary.every_month')}`
        : t('checkout.summary.months', { multiplier: currentCycleMultiplier });
    const [monthlyPrice, fullTermPrice] = controller.getSummeryPrices();
    const summaryItems: React.ReactElement[] = [];

    const checkoutRowLabelProps: CheckoutRowLabelProps = {
      formatCurrency,
      cycleString,
      cycleMonthlyPrice: monthlyPrice,
      monthlyFullPrice,
      showDiscountSavings,
      showStrikedPrice,
      currentCycle: currentProductCycle,
    };

    summaryItems.push(
      <div key='product'>
        <Text weight='bold'>{currentProduct.productName}</Text>
        <Checkout.Summary.Row
          labelDataHook='product_cycle_lable'
          valueDataHook='product_cycle_value'
          key={1}
          value={formatCurrency!(fullTermPrice)}
          label={<CheckoutRowLabel props={checkoutRowLabelProps} />}
        />
      </div>,
    );
    return summaryItems;
  };

  const shouldShowCreditExplanation = () => {
    const { currentPlanDetails, availableCredit } = appContext;
    const { daysLeft, pricePerDay } = creditExplanationTooltipData(
      currentPlanDetails,
      availableCredit,
    );
    return !!(
      isSecondUpgrade &&
      currentPlanDetails &&
      availableCredit &&
      daysLeft >= 1 &&
      pricePerDay >= 0.01
    );
  };

  return (
    <div
      className={classNames(props.className, s.checkoutContainer, themeName, {
        [s.overrideCheckoutBorders]: !experiments.enabled(
          Experiments.EnablePaymentTypeFiltersSpec,
        ),
      })}
      data-hook='checkout-page'
      data-testid='checkout-page'
    >
      <Checkout
        currencySettings={{
          [appContext.currencyCode]: appContext.currencySettings,
        }}
        locale={appContext.locale}
        onInitError={(props) => {
          biLogger.logCheckoutLoadFailed({
            packagePickerType: packagePickerType,
            productGuid: currentProduct.guid,
            siteId: appContext.siteGuid,
            failReason: props?.errorInfo?.failureReason,
            IsCartFlow: false,
            cartFlow: AppFlow.CLASSIC,
            eventAggregator: props?.orderPageEventAggregator,
          });
          handleInitError(props);
        }}
        beforeInit={beforeInit}
        orderPageCreation={initOrderPage}
        onInitSuccess={checkoutInitCompleted}
        onSubmitError={handlePurchaseError}
        onSubmitSuccess={paymentSuccessful}
        onSubmitPending={onSubmitPending}
        onBeforeSubmit={beforeSubmit}
        appName='premium-purchase-plan'
        selectedPaymentMethod={({ selectedPaymentMethod }) => {
          const showCustomDisclaimer =
            experiments.enabled(Experiments.MoneyBackGuaranteeInSepa) ||
            selectedPaymentMethod !== 'sepa';
          setShouldShowCustomDisclaimer(showCustomDisclaimer);
        }}
        reporterData={{
          user: userDetails,
          siteGuid: appContext.siteGuid,
        }}
        summaryProps={{
          renderCustomDisclaimer: () => <TermOfUse />,
          renderItems: (items: SummaryItem[]) =>
            getSummaryItems(currencyToString, items),
          renderCreditExplanation: () =>
            shouldShowCreditExplanation() && <CreditExplanation />,
          renderCustomFooter: shouldShowCustomDisclaimer
            ? () => <CheckoutSummaryFooter />
            : () => null,
          allowManualCoupon: true,
          injectedCouponDisplayName: (couponName) => getCouponLabel(couponName),
        }}
        httpClient={new HttpClient()}
      />
      <DoublePurchaseModal
        isOpen={isDoublePurchaseModalOpened}
        onClose={closeDoublePurchaseModal}
        additionalInfo={additionalDoublePurchaseInfo}
      />
      <PageFooter>
        {isEditorXSpecialDiscountEnabled && (
          <div>
            <Text
              weight='thin'
              size='tiny'
              dataHook='special-discount-disclaimer'
              secondary
            >
              {t('special_discount_disclaimer')}
            </Text>
          </div>
        )}
        <Text weight='thin' size='tiny' dataHook='legal-disclaimer' secondary>
          {isVoucherDisclaimerHidden
            ? ''
            : t('page_footer.checkout_disclaimer')}
        </Text>
      </PageFooter>
    </div>
  );
};

export const CheckoutPage = withInjectedRootStore(
  withGtmLogger(withExperiments(withTranslation()(observer(CheckoutPageComp)))),
);
